<template>
  <VueFinalModal
    :esc-to-close="true"
    class="flex justify-center items-center"
    content-class="relative flex flex-col w-[600px] mx-auto bg-white border rounded-lg"
  >
    <div class="p-6">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h3 class="text-xl font-semibold text-gray-900">
            {{ t('modals.village_survey.village_survey_info') }}
          </h3>
          <p class="mt-1 text-sm text-gray-500">
            {{ villageData.name }}
          </p>
        </div>
        <button 
          @click.stop="closeModal()"
          class="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <div class="space-y-4">
        <!-- Village Details -->
        <div class="bg-gray-50 rounded-lg p-4">
          <h4 class="text-sm font-medium text-gray-700 mb-3">{{ t('modals.village_survey.village_details') }}</h4>
          <dl class="grid grid-cols-1 gap-2">
            <div class="flex justify-between">
              <dt class="text-sm text-gray-500">{{ t('modals.village_survey.village_name') }}:</dt>
              <dd class="text-sm font-medium text-gray-900">{{ villageData.name }}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-sm text-gray-500">{{ t('modals.village_survey.admin_code') }}:</dt>
              <div class="admin-code-wrapper">
                <dd
                  class="text-sm font-medium text-gray-900 truncate-admin-code"
                  :title="villageData.walp?.admin4Code || villageData.admin4Code"
                  @click="copyAdminCode"
                >
                  {{ villageData.walp?.admin4Code || villageData.admin4Code }}
                </dd>
                <span v-if="copied" class="copied-tooltip">{{ t('modals.village_survey.copied') }}</span>
              </div>
            </div>
          </dl>
        </div>

        <!-- Survey Form -->
        <div class="bg-white rounded-lg p-4 border space-y-4">
          <div class="flex flex-col gap-4">
            <div class="flex items-center gap-4">
              <label class="block text-sm font-medium text-gray-700">Are there Christians in this City/Village?</label>
              <label class="ml-2"><input type="radio" :value="true" v-model="areChristians" /> Yes</label>
              <label class="ml-2"><input type="radio" :value="false" v-model="areChristians" /> No</label>
            </div>
            <div v-if="areChristians === true" class="flex items-center gap-2">
              <label class="block text-sm font-medium text-gray-700">If yes, how many?</label>
              <input type="number" min="0" v-model.number="numChristians" class="border rounded px-2 py-1 w-24" />
            </div>
          </div>
        </div>

        <!-- Gatherings Section -->
        <div v-if="areChristians === true" class="bg-white rounded-lg p-4 border space-y-4 mt-4">
          <div v-if="!showGatheringForm" class="flex justify-between items-center mb-2">
            <h4 class="text-sm font-medium text-gray-700">Gatherings in this Village</h4>
            <button class="text-blue-600 font-semibold" @click="openAddGathering()">+ Add Church</button>
          </div>
          <div v-if="!showGatheringForm" class="overflow-x-auto overflow-y-auto max-h-64">
            <table class="min-w-full w-full text-xs border">
              <thead>
                <tr class="bg-gray-100">
                  <th class="px-2 py-1">#</th>
                  <th class="px-2 py-1">Type</th>
                  <th class="px-2 py-1">Name</th>
                  <th class="px-2 py-1">Believers</th>
                  <th class="px-2 py-1">Year Started</th>
                  <th class="px-2 py-1">Month Started</th>
                  <th class="px-2 py-1">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(g, idx) in gatherings" :key="g.record_uuid">
                  <td class="px-2 py-1">{{ idx + 1 }}</td>
                  <td class="px-2 py-1">Church</td>
                  <td class="px-2 py-1 truncate max-w-[120px] whitespace-nowrap" :title="g.name">{{ g.name }}</td>
                  <td class="px-2 py-1">{{ g.num_believers }}</td>
                  <td class="px-2 py-1">{{ g.year_started }}</td>
                  <td class="px-2 py-1">{{ g.month_started }}</td>
                  <td class="px-2 py-1 flex gap-2">
                    <button class="text-blue-600 text-sm" @click="openAddGathering(g, idx)">Edit</button>
                    <button class="text-red-600 text-sm" @click="removeGathering(idx)">Delete</button>
                  </td>
                </tr>
                <tr v-if="gatherings.length === 0">
                  <td colspan="7" class="text-center text-gray-400 py-2">No gatherings yet.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else>
            <div class="space-y-4">
              <div class="flex gap-2 mb-2">
                <button
                  class="flex-1 rounded px-2 py-1 font-semibold border"
                  :class="gatheringForm.church_type === 'church' ? 'bg-blue-600 text-white border-blue-600' : 'bg-blue-100 text-blue-700 border-blue-100'"
                  disabled
                >
                  <span class="inline-flex items-center">
                    Church
                  </span>
                </button>
                <button
                  class="flex-1 rounded px-2 py-1 font-semibold border bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                  disabled
                >
                  <span class="inline-flex items-center">
                    Church Network
                  </span>
                </button>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Church Name</label>
                <input v-model="gatheringForm.name" class="border rounded px-2 py-1 w-full" placeholder="Enter church name" required />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Year Start</label>
                <input v-model="gatheringForm.year_started" type="number" min="1900" max="2100" class="border rounded px-2 py-1 w-full" placeholder="Enter Approximate Year Start" required />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Month Start</label>
                <input v-model="gatheringForm.month_started" type="number" min="1" max="12" class="border rounded px-2 py-1 w-full" placeholder="Enter Month (1-12)" required />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Believers</label>
                <input v-model="gatheringForm.num_believers" type="number" min="0" class="border rounded px-2 py-1 w-full" placeholder="Enter number of believers" required />
              </div>

              <div class="flex justify-end gap-2 mt-4">
                <button class="px-4 py-2 rounded bg-gray-200 text-gray-700" @click="cancelGathering">Cancel</button>
                <button 
                  class="px-4 py-2 rounded bg-blue-600 text-white" 
                  @click="saveGathering"
                  :disabled="!gatheringForm.name || !gatheringForm.num_believers || !gatheringForm.year_started || !gatheringForm.month_started"
                  :class="{ 'opacity-50 cursor-not-allowed': !gatheringForm.name || !gatheringForm.num_believers || !gatheringForm.year_started || !gatheringForm.month_started }"
                >
                  {{ editingGatheringIndex !== null ? t('modals.village_survey.update_church') : t('modals.village_survey.add_church') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Survey Actions -->
        <div v-if="isBatchUpdatable" class="space-y-2">
          <button 
            class="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            @click="isEditingExistingSurvey ? updateVillageSurvey() : createVillageSurvey()"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
            </svg>
            {{ isEditingExistingSurvey ? t('modals.village_survey.update_survey') : t('modals.village_survey.create_survey') }}
          </button>
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<script setup>
import { VueFinalModal } from 'vue-final-modal'
import { onMounted, ref, computed, watch } from 'vue'
import i18n from '@utils/i18n'
import FlashMessageService from '@services/FlashMessages'
import GlobalEventBus from '@services/GlobalEventBus'
import ModalService from '@services/ModalService'

const { t } = i18n.global

const props = defineProps({
  params: Object,
  manualMode: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close'])

let villageData = ref({})
let batchData = ref({})
const copied = ref(false)
let copyTimeout = null

// Add form state
const areChristians = ref(true)
const numChristians = ref("")

const gatherings = ref([])
const showGatheringForm = ref(false)
const editingGatheringIndex = ref(null)

const gatheringForm = ref({
  // selectedChurchId: '',
  name: '',
  year_started: '',
  num_believers: '',
  church_type: 'church',
  num_churches_in_network: 0,
  lat: null,
  lng: null,
  month_started: null,
})


onMounted(() => {
  if (props.params && props.params.village) {
    villageData.value = props.params.village
  }
  if (props.params && props.params.batchData) {
    batchData.value = props.params.batchData
  }
  // Pre-populate form fields if editing an existing survey
  if (props.params && props.params.survey) {
    console.log('editing survey:', props.params.survey)

    const survey = props.params.survey

    areChristians.value = Boolean(survey.are_christians)
    numChristians.value = survey.num_christians || 0
    
    // Populate gatherings if they exist
    if (survey.churches && survey.churches.length > 0) {
      gatherings.value = survey.churches.map(church => ({
        record_uuid: church.record_uuid,
        church_type: church.church_type || 'church',
        name: church.name || '',
        year_started: church.year_started || null,
        num_believers: church.num_believers || 0,
        num_churches_in_network: church.num_churches_in_network || 0,
        lat: church.lat || null,
        lng: church.lng || null,
        month_started: church.month_started || null
      }))
    }
  }
})

// Get surveyBatchController from params
const surveyBatchController = computed(() => {
  return props.params?.surveyBatchController
})

// // Get churches from params
// const churches = computed(() => {
//   return props.params?.batchData?.churches || []
// })

// Check if batch is ready for survey or if we're in manual mode
const isBatchUpdatable = computed(() => {
  return batchData.value.batch?.state === 'ready_for_survey' || 
         batchData.value.batch?.state == "pending_approval" ||
         props.params.manualMode
})

// Check if we're editing an existing survey
const isEditingExistingSurvey = computed(() => {
  // In manual mode, record_uuid is null (generated by Kestrel internally),
  // so check for the survey object itself from localStorage
  if (props.params?.manualMode) {
    return !!props.params?.survey
  }
  return props.params?.survey && props.params.survey.record_uuid
})

watch(areChristians, (newVal) => {
  if (newVal === false) {
    numChristians.value = 0
  }
})

function closeModal() {
  // Use ModalService to close the modal instead of emitting to prevent bubbling
  // This allows us to have nested modals
  ModalService.close('VillageSurveyModal')
}

function openAddGathering(gathering = null, idx = null) {
  if (gathering) {
    gatheringForm.value = { ...gathering }
    editingGatheringIndex.value = idx
  } else {
    gatheringForm.value = {
      // selectedChurchId: '',
      name: '',
      year_started: '',
      num_believers: '',
      church_type: 'church',
      num_churches_in_network: 0,
      lat: null,
      lng: null,
      month_started: null,
    }
    editingGatheringIndex.value = null
  }
  showGatheringForm.value = true
}

function saveGathering() {
  // Validate required fields
  if (!gatheringForm.value.name || !gatheringForm.value.num_believers) {
    return
  }
  
  const gatheringData = {
    ...gatheringForm.value,
    year_started: gatheringForm.value.year_started ? parseInt(gatheringForm.value.year_started) : null,
    num_believers: parseInt(gatheringForm.value.num_believers) || 0,
    num_churches_in_network: 0,
    // TODOO: how are we supposed to get lat/long for this church that gets inputted?
    lat: gatheringForm.value.lat,
    lng: gatheringForm.value.lng,
    month_started: gatheringForm.value.month_started ? parseInt(gatheringForm.value.month_started) : null,
    remote_created_at: gatheringForm.value.remote_created_at || new Date().toISOString(),
  }

  if (editingGatheringIndex.value !== null) {
    // Update existing
    gatherings.value[editingGatheringIndex.value] = gatheringData
  } else {
    // Add new
    gatherings.value.push(gatheringData)
  }
  
  showGatheringForm.value = false
  editingGatheringIndex.value = null
}

// When canceling, reset editingGatheringIndex
function cancelGathering() {
  showGatheringForm.value = false
  editingGatheringIndex.value = null
}

function copyAdminCode() {
  const adminCode = villageData.value.walp?.admin4Code || villageData.value.admin4Code
  if (!adminCode) return

  navigator.clipboard.writeText(adminCode)
    .then(() => {
      copied.value = true
      if (copyTimeout) clearTimeout(copyTimeout)
      copyTimeout = setTimeout(() => {
        copied.value = false
      }, 1000)
    })
}

function buildVillageSurveyPayload() {
  return {
    // Include record_uuid if editing an existing survey
    record_uuid: props.params?.survey?.record_uuid || null,
    admin_code: villageData.value.walp?.admin4Code || villageData.value.admin4Code,
    
    attributes: {
      are_christians: areChristians.value,
      num_christians: numChristians.value,
    },

    relations: {
      churches: gatherings.value.map(g => ({
        record_uuid: g.record_uuid,
        attributes: {
          church_type: g.church_type,
          lat: g.lat,
          lng: g.lng,
          num_churches_in_network: g.num_churches_in_network,
          name: g.name,
          year_started: g.year_started,
          month_started: g.month_started,
          num_believers: g.num_believers,
        }
      }))
    }
  }
}

function buildVillageSurveyChurchPayload() {
  return {
    record_uuid: props.params?.survey?.record_uuid || null,
    village_survey_record_uuid: props.params?.survey?.record_uuid || null,
    attributes: {
      church_type: gatheringForm.value.church_type,
      lat: gatheringForm.value.lat,
      lng: gatheringForm.value.lng,
      name: gatheringForm.value.name,
      year_started: gatheringForm.value.year_started,
      month_started: gatheringForm.value.month_started,
      num_believers: gatheringForm.value.num_believers
    }
  }
}

async function createVillageSurvey() {
  try {
    const surveyPayload = buildVillageSurveyPayload()
    console.log("buildVillageSurveyPayload() in createVillageSurvey()", surveyPayload)

    if (props.params.manualMode) {
      let villageSurveyAttributes = {
        are_christians: areChristians.value,
        num_christians: numChristians.value,
      }
      
      let churches = gatherings.value.map(g => ({
        church_type: g.church_type,
        lat: g.lat,
        lng: g.lng,
        num_churches_in_network: g.num_churches_in_network,
        name: g.name,
        year_started: g.year_started,
        month_started: g.month_started,
        num_believers: g.num_believers,
      }))

      surveyBatchController.value.buildAdhocVillageSurvey(
        villageData.value.walp.id,
        villageSurveyAttributes,
        churches
      )

      // Dispatch event to create survey in localStorage
      GlobalEventBus.dispatch('village-survey-upserted', { survey: surveyPayload, village: villageData.value })
      closeModal()
      FlashMessageService.success('Village survey saved locally')
    } else {
      // Normal mode - create survey immediately
      if (!surveyBatchController) { return }
      await surveyBatchController.value.createVillageSurvey(surveyPayload)

      // Dispatch event to update the parent component's survey status colors
      GlobalEventBus.dispatch('village-survey-upserted', { survey: surveyPayload, village: villageData.value })
      
      closeModal()
      FlashMessageService.success('Village survey created successfully')
    }
  } catch (error) {
    console.error('Error creating village survey:', error)
    FlashMessageService.error('Failed to create village survey: ' + error.message)
  }
}

async function updateVillageSurvey() {
  try {
    const surveyPayload = buildVillageSurveyPayload()
    console.log("buildVillageSurveyPayload() in updateVillageSurvey()", surveyPayload)

    if (props.params.manualMode) {
      // Remove old adhoc survey from batch controller and build updated one
      const adminCode = villageData.value.walp?.admin4Code || villageData.value.admin4Code
      surveyBatchController.value.adhocVillageSurveys =
        surveyBatchController.value.adhocVillageSurveys.filter(s => s.admin_code !== adminCode)

      let villageSurveyAttributes = {
        are_christians: areChristians.value,
        num_christians: numChristians.value,
      }

      let churches = gatherings.value.map(g => ({
        church_type: g.church_type,
        lat: g.lat,
        lng: g.lng,
        num_churches_in_network: g.num_churches_in_network,
        name: g.name,
        year_started: g.year_started,
        month_started: g.month_started,
        num_believers: g.num_believers,
      }))

      surveyBatchController.value.buildAdhocVillageSurvey(
        villageData.value.walp.id,
        villageSurveyAttributes,
        churches
      )

      GlobalEventBus.dispatch('village-survey-upserted', { survey: surveyPayload, village: villageData.value })
      closeModal()
      FlashMessageService.success('Village survey updated locally')
    } else {
      // Normal mode - update survey immediately
      if (!surveyBatchController) { return }
      await surveyBatchController.value.updateVillageSurvey(surveyPayload)
      GlobalEventBus.dispatch('village-survey-upserted', { survey: surveyPayload, village: villageData.value })
      closeModal()
      FlashMessageService.success('Village survey updated successfully')
    }
  } catch (error) {
    console.error('Error updating village survey:', error)
    FlashMessageService.error('Failed to update village survey: ' + error.message)
  }
}

async function createVillageSurveyChurch() {
  if (!surveyBatchController) { return }
  try {
    await surveyBatchController.value.createVillageSurveyChurch(buildVillageSurveyChurchPayload())
    FlashMessageService.success('Village survey church created successfully')
  } catch (error) {
    console.error('Error creating village survey church:', error)
    FlashMessageService.error('Failed to create village survey church: ' + error.message)
  }
}

async function updateVillageSurveyChurch() {
  if (!surveyBatchController) { return }
  try {
    await surveyBatchController.value.updateVillageSurveyChurch(buildVillageSurveyChurchPayload())
    FlashMessageService.success('Village survey church updated successfully')
  } catch (error) {
    console.error('Error updating village survey church:', error)
    FlashMessageService.error('Failed to update village survey church: ' + error.message)
  }
}

function removeGathering(index) {
  gatherings.value.splice(index, 1)
}
</script>

<style>
.vm--modal {
  overflow: initial !important;
}
.admin-code-wrapper {
  position: relative;
  display: inline-block;
}
.truncate-admin-code {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  vertical-align: bottom;
  cursor: pointer;
  position: relative;
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
}
.copied-tooltip {
  position: absolute;
  left: 50%;
  top: -1.5em;
  transform: translateX(-50%);
  background: #2563eb;
  color: #fff;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  white-space: nowrap;
  z-index: 10;
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}
</style> 